import React, { useState, useEffect } from 'react';
import { Plus, Check, Trash2, ShieldCheck, User as UserIcon } from 'lucide-react';
import { safeFetch } from '../../lib/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TodosTab() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending | done | all
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [due, setDue] = useState('');

  const fetchTodos = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = filter === 'all'
        ? `${API_URL}/api/todos`
        : `${API_URL}/api/todos?status=${filter}`;
      const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setTodos(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTodos(); }, [filter]);

  const addTodo = async () => {
    if (!title.trim()) return;
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/todos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: title.trim(), description: desc.trim(), due_date: due || undefined })
    });
    if (result.ok) {
      setTitle(''); setDesc(''); setDue('');
      setAdding(false);
      fetchTodos();
    }
  };

  const toggle = async (todo) => {
    const next = todo.status === 'done' ? 'pending' : 'done';
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/todos/${todo.id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: next })
    });
    fetchTodos();
  };

  const remove = async (todo) => {
    if (!window.confirm(`Delete "${todo.title}"?`)) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/todos/${todo.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchTodos();
  };

  return (
    <div className="space-y-3" data-testid="todos-tab">
      {/* Filter pills */}
      <div className="bg-white rounded-xl p-2 shadow-sm flex items-center gap-1">
        {['pending', 'done', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold capitalize ${filter === f ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
            data-testid={`todo-filter-${f}`}
          >{f}</button>
        ))}
        <button
          onClick={() => setAdding(a => !a)}
          className="ml-1 p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded"
          data-testid="todo-add-toggle"
        >
          <Plus size={14} />
        </button>
      </div>

      {/* Add form */}
      {adding && (
        <div className="bg-white rounded-xl p-3 shadow-sm space-y-2" data-testid="todo-add-form">
          <input
            type="text" placeholder="Task title"
            value={title} onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm"
            data-testid="todo-title-input"
          />
          <textarea
            placeholder="Description (optional)"
            value={desc} onChange={(e) => setDesc(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded text-sm"
          />
          <div className="flex items-center gap-2">
            <input
              type="date" value={due} onChange={(e) => setDue(e.target.value)}
              className="flex-1 px-3 py-2 border rounded text-sm"
            />
            <button
              onClick={addTodo}
              disabled={!title.trim()}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-sm font-semibold"
              data-testid="todo-create-btn"
            >Add</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-4 text-center text-sm text-gray-500">Loading…</p>
        ) : todos.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500">No tasks {filter !== 'all' && `(${filter})`}.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {todos.map(t => (
              <li key={t.id} className="px-3 py-2 flex items-start gap-2" data-testid={`todo-row-${t.id}`}>
                <button
                  onClick={() => toggle(t)}
                  className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${t.status === 'done' ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 hover:border-emerald-400'}`}
                  data-testid={`todo-toggle-${t.id}`}
                  title={t.status === 'done' ? 'Mark as pending' : 'Mark as done'}
                >
                  {t.status === 'done' && <Check size={12} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm ${t.status === 'done' ? 'line-through text-gray-400' : 'text-gray-900 font-medium'}`}>{t.title}</span>
                    {t.created_by_role === 'admin' ? (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] rounded font-semibold uppercase tracking-wide">
                        <ShieldCheck size={10} /> Admin
                      </span>
                    ) : t.created_by && t.created_by !== t.assignee_id ? (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-semibold uppercase tracking-wide">
                        <ShieldCheck size={10} /> Senior
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded font-semibold uppercase tracking-wide">
                        <UserIcon size={10} /> Personal
                      </span>
                    )}
                  </div>
                  {t.description && <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>}
                  <div className="flex items-center gap-3 mt-0.5 text-[10px] text-gray-400">
                    {t.created_by_name && t.created_by !== t.assignee_id && <span>by {t.created_by_name}</span>}
                    {t.due_date && <span>due {t.due_date}</span>}
                  </div>
                </div>
                <button
                  onClick={() => remove(t)}
                  className="text-gray-400 hover:text-red-600 p-1"
                  data-testid={`todo-delete-${t.id}`}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
